import { useState } from 'react';
import { useMessages } from './i18n/context';
import { blockTypeName } from './blocks/blockTypeName';
import type { ComponentResponse } from './api/types';

export interface NodeCallbacks {
  onAddChild: (parentId: string) => void;
  onRename: (componentId: string, title: string) => void;
  onMove: (componentId: string, index: number, parentId?: string) => void;
  onRemove: (componentId: string) => void;
}

/**
 * One block in the plan tree, rendered recursively. Every action posts one operation and
 * the whole tree re-renders from the server's answer — no local tree surgery (ADR-025).
 * `index`/`siblingCount`/`parentId` situate the block for the move operation.
 */
export function ComponentNode({
  component,
  index,
  siblingCount,
  parentId,
  busy,
  callbacks,
}: {
  component: ComponentResponse;
  index: number;
  siblingCount: number;
  parentId?: string;
  busy: boolean;
  callbacks: NodeCallbacks;
}) {
  const t = useMessages();
  const g = t.grove;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const title = component.properties['title'] ?? component.id;

  return (
    <li className="block-node card">
      <div className="block-heading">
        <div className="block-title">
          <strong>{title}</strong> <span className="block-type">({blockTypeName(t, component.type)})</span>
        </div>
        <div className="block-actions" role="group" aria-label={g.actionsFor(title)}>
          <button
            className="button-quiet"
            aria-label={t.node.moveUp(title)}
            disabled={busy || index === 0}
            onClick={() => callbacks.onMove(component.id, index - 1, parentId)}
          >
            {t.node.up}
          </button>
          <button
            className="button-quiet"
            aria-label={t.node.moveDown(title)}
            disabled={busy || index === siblingCount - 1}
            onClick={() => callbacks.onMove(component.id, index + 1, parentId)}
          >
            {t.node.down}
          </button>
          <button
            className="button-quiet"
            aria-label={t.node.rename(title)}
            disabled={busy}
            onClick={() => {
              setDraft(title);
              setEditing(true);
            }}
          >
            {t.node.renameButton}
          </button>
          <button
            className="button-secondary"
            aria-label={t.node.addInside(title)}
            disabled={busy}
            onClick={() => callbacks.onAddChild(component.id)}
          >
            {t.node.addInsideButton}
          </button>
          <button
            className="button-danger"
            aria-label={t.node.remove(title)}
            disabled={busy}
            onClick={() => callbacks.onRemove(component.id)}
          >
            {t.node.removeButton}
          </button>
        </div>
      </div>
      {editing && (
        <form
          className="inline-form"
          aria-label={t.node.renameButton}
          onSubmit={(e) => {
            e.preventDefault();
            setEditing(false);
            callbacks.onRename(component.id, draft);
          }}
        >
          <input
            aria-label={t.node.newTitleFor(title)}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button className="button-primary" type="submit">{t.node.save}</button>
          <button className="button-quiet" type="button" onClick={() => setEditing(false)}>
            {t.node.cancel}
          </button>
        </form>
      )}
      {component.children.length > 0 && (
        <ul className="block-tree">
          {component.children.map((child, childIndex) => (
            <ComponentNode
              key={child.id}
              component={child}
              index={childIndex}
              siblingCount={component.children.length}
              parentId={component.id}
              busy={busy}
              callbacks={callbacks}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
